import { useState } from 'react'
import { Link, usePage } from '@inertiajs/react'
import { Toaster } from 'sonner'
import { usePostHogIdentify } from '@/hooks/usePostHogIdentify'
import { resetPostHog } from '@/lib/posthog'
import {
  Calendar,
  Users,
  Scissors,
  Settings,
  LayoutDashboard,
  Building2,
  UserCog,
  DoorOpen,
  LogOut,
  Menu,
  MessageSquare,
  ChevronsUpDown,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { BranchPicker } from '@/components/BranchPicker'
import { BillingProvider } from '@/contexts/BillingContext'
import { TrialBanner } from '@/components/billing/TrialBanner'
import { UsageBanner } from '@/components/billing/UsageBanner'
import { SupportButton } from '@/components/SupportButton'
import type { CurrentUser, Organization, Business, Role } from '@/types'
import type { PageProps } from '@/types'

interface NavItem {
  label: string
  href: string
  icon: React.ComponentType<{ className?: string }>
  roles: Role[]
}

function getNavItems(orgSlug: string, hasInbox: boolean): NavItem[] {
  return [
    {
      label: 'Inicio',
      href: `/${orgSlug}/dashboard`,
      icon: LayoutDashboard,
      roles: ['owner', 'admin'],
    },
    {
      label: 'Calendario',
      href: `/${orgSlug}/calendar`,
      icon: Calendar,
      roles: ['owner', 'admin', 'staff'],
    },
    {
      label: 'Clientes',
      href: `/${orgSlug}/clients`,
      icon: Users,
      roles: ['owner', 'admin', 'staff'],
    },
    ...(hasInbox
      ? [
          {
            label: 'Inbox',
            href: `/${orgSlug}/inbox`,
            icon: MessageSquare,
            roles: ['owner', 'admin', 'staff'] as Role[],
          },
        ]
      : []),
    {
      label: 'Servicios',
      href: `/${orgSlug}/services`,
      icon: Scissors,
      roles: ['owner', 'admin', 'staff'],
    },
    {
      label: 'Equipo',
      href: `/${orgSlug}/team`,
      icon: UserCog,
      roles: ['owner', 'admin', 'staff'],
    },
    {
      label: 'Espacios',
      href: `/${orgSlug}/spaces`,
      icon: DoorOpen,
      roles: ['owner', 'admin'],
    },
    {
      label: 'Sucursales',
      href: `/${orgSlug}/businesses`,
      icon: Building2,
      roles: ['owner', 'admin'],
    },
    {
      label: 'Configuración',
      href: `/${orgSlug}/settings`,
      icon: Settings,
      roles: ['owner', 'admin'],
    },
  ]
}

interface DashboardLayoutProps {
  children: React.ReactNode
  currentUser: CurrentUser | null
  currentOrganization: Organization | null
  currentBusiness: Business | null
  orgSlug: string
}

function getUserRole(
  currentUser: CurrentUser | null,
  currentOrganization: Organization | null,
  currentBusiness: Business | null
): Role {
  if (!currentUser) return 'staff'

  if (currentOrganization) {
    const orgRole = currentUser.org_roles.find(
      (r) => r.organization.id === currentOrganization.id
    )
    if (orgRole) {
      return orgRole.role
    }
  }

  if (currentBusiness) {
    const staffAssignment = currentUser.staff_assignments.find(
      (s) => s.business.id === currentBusiness.id
    )
    if (staffAssignment) {
      return 'staff'
    }
  }

  return 'staff'
}

export function DashboardLayout({
  children,
  currentUser,
  currentOrganization,
  currentBusiness,
  orgSlug,
}: DashboardLayoutProps) {
  const { impersonation, businesses, kapso_inbox } = usePage<PageProps>().props
  usePostHogIdentify()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const role = getUserRole(currentUser, currentOrganization, currentBusiness)
  const hasInbox = Boolean((kapso_inbox as { available?: boolean } | undefined)?.available)
  const navItems = getNavItems(orgSlug, hasInbox)
  const filteredNavItems = navItems.filter((item) => item.roles.includes(role))

  const canSwitchOrganization =
    (currentUser?.accessible_organizations?.length ?? 0) > 1
  const orgLabel =
    currentOrganization?.name ?? currentBusiness?.name ?? 'Quetzal'

  const sidebarContent = (
    <>
      {/* Logo / Business Name */}
      <div className="flex h-16 items-center justify-between border-b border-sidebar-border px-6">
        {canSwitchOrganization ? (
        <Link href="/organizations/select" className="group flex min-w-0 items-center gap-2" title="Cambiar organización">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
            <Building2 className="h-4 w-4" />
          </div>
          <span className="truncate font-semibold text-sidebar-foreground">
            {orgLabel}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-sidebar-foreground" />
        </Link>
        ) : (
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
            <Building2 className="h-4 w-4" />
          </div>
          <span className="truncate font-semibold text-sidebar-foreground">
            {orgLabel}
          </span>
        </div>
        )}
        <button
          onClick={() => setSidebarOpen(false)}
          className="rounded-lg p-2 text-sidebar-foreground hover:bg-sidebar-accent lg:hidden"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Branch Picker */}
      <BranchPicker
        businesses={businesses || []}
        currentBusiness={currentBusiness}
        orgSlug={orgSlug}
      />

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-4">
        {filteredNavItems.map((item) => {
          const Icon = item.icon
          const isActive = window.location.pathname.startsWith(item.href)

          return (
            <Link
              key={item.href}
              href={item.href}
              onClick={() => setSidebarOpen(false)}
              className={cn(
                'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
              )}
            >
              <Icon className="h-4 w-4" />
              {item.label}
            </Link>
          )
        })}
      </nav>

      {/* User Menu */}
      <div className="border-t border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-sidebar-accent text-sm font-medium text-sidebar-accent-foreground">
            {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          <div className="flex-1 truncate">
            <p className="truncate text-sm font-medium text-sidebar-foreground">
              {currentUser?.name || 'Usuario'}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {role === 'owner' ? 'Propietario' : role === 'admin' ? 'Administrador' : 'Personal'}
            </p>
          </div>
          <Link
            href="/users/sign_out"
            method="delete"
            as="button"
            onClick={resetPostHog}
            className="rounded-lg p-2 text-sidebar-foreground hover:bg-sidebar-accent"
          >
            <LogOut className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </>
  )

  return (
    <BillingProvider>
      <div className="flex h-screen bg-background">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 bg-black/50 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Sidebar - Desktop */}
        <aside className="hidden w-64 flex-col border-r border-sidebar-border bg-sidebar lg:flex">
          {sidebarContent}
        </aside>

        {/* Sidebar - Mobile */}
        <aside
          className={cn(
            'fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-sidebar-border bg-sidebar transition-transform duration-300 lg:hidden',
            sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          )}
        >
          {sidebarContent}
        </aside>

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {impersonation?.active && (
            <div className="border-b border-amber-300 bg-amber-100 px-4 py-2 text-sm text-amber-900">
              <div className="mx-auto flex max-w-6xl items-center justify-between gap-3">
                <span>
                  Estás impersonando una cuenta (iniciado por {impersonation.impersonator_name}).
                </span>
                <Link
                  href="/impersonation"
                  method="delete"
                  as="button"
                  className="rounded bg-amber-900 px-2 py-1 text-xs font-medium text-amber-100 hover:bg-amber-800"
                >
                  Salir de impersonación
                </Link>
              </div>
            </div>
          )}

          {/* Usage Banner */}
          <TrialBanner orgSlug={orgSlug} />
          <UsageBanner orgSlug={orgSlug} />

          {/* Mobile header */}
          <header className="flex h-16 items-center gap-4 border-b border-border bg-background px-4 lg:hidden">
            <button
              onClick={() => setSidebarOpen(true)}
              className="rounded-lg p-2 text-foreground hover:bg-accent"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="font-semibold">{currentBusiness?.name ?? 'Quetzal'}</span>
          </header>

          <main className="flex-1 overflow-auto">
            <div className="h-full p-4 lg:p-8">{children}</div>
          </main>
        </div>

        <SupportButton />
        <Toaster position="top-right" richColors />
      </div>
    </BillingProvider>
  )
}
